import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Quotation,
  AccommodationGroup,
  FlightGroup
} from './types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { X, ImagePlus, Globe, MapPin, Calendar, Check, Map, DollarSign, CircleDot, Navigation, Settings, Building, Layers, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { formatSafeDate } from "@/lib/date-utils";
import { Label } from '@/components/ui/label';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { useToast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { QuotationPriceTab } from './PriceTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RecommendationsTab } from './RecommendationsTab';
import { tripService } from '@/lib/supabase/trips';
import { clientService } from '@/lib/supabase/clients';
import { QuotationAccommodationsTab } from './AccommodationsTab';
import { QuotationFlightsTab } from './FlightsTab';
import { CustomTab } from './CustomTab';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { GripVertical, Pencil, Check as CheckIcon, X as XIcon, Plus } from 'lucide-react';

// Custom styled section title with orange underline and icon
const StyledSectionTitle = ({ 
  children, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.HTMLAttributes<HTMLHeadingElement> & { icon?: React.ElementType }) => (
  <h3 
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3", className)}
    {...props}
  >
    <Icon className="h-4 w-4 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </h3>
);

interface Trip {
  id: string;
  title?: string;
  clientIds: string[];
  destination: string[] | string;
  startDate?: Date | null;
  endDate?: Date | null;
  status: 'quotation' | 'itinerary' | 'day-by-day';
  notes?: string;
}

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
}

interface BasicInfoFormProps {
  quotation: Quotation;
  handleChange: (field: keyof Quotation, value: any) => void;
  isEditing?: boolean;
  onCancel?: () => void;
  saveQuotation?: () => Promise<boolean>;
  migrateOrphanPages?: () => boolean;
}

// Lista de países
const countries = [
  "Afeganistão", "África do Sul", "Albânia", "Alemanha", "Andorra", "Angola", "Antígua e Barbuda", "Arábia Saudita", 
  "Argélia", "Argentina", "Armênia", "Austrália", "Áustria", "Azerbaijão", "Bahamas", "Bangladesh", "Barbados", 
  "Barein", "Bélgica", "Belize", "Benin", "Bielorrússia", "Bolívia", "Bósnia e Herzegovina", "Botsuana", "Brasil", 
  "Brunei", "Bulgária", "Burkina Faso", "Burundi", "Butão", "Cabo Verde", "Camarões", "Camboja", "Canadá", "Catar", 
  "Cazaquistão", "Chade", "Chile", "China", "Chipre", "Colômbia", "Comores", "Congo", "Coreia do Norte", "Coreia do Sul", 
  "Costa do Marfim", "Costa Rica", "Croácia", "Cuba", "Dinamarca", "Djibouti", "Dominica", "Egito", "El Salvador", 
  "Emirados Árabes Unidos", "Equador", "Eritreia", "Eslováquia", "Eslovênia", "Espanha", "Estados Unidos", "Estônia", 
  "Etiópia", "Fiji", "Filipinas", "Finlândia", "França", "Gabão", "Gâmbia", "Gana", "Geórgia", "Granada", "Grécia", 
  "Guatemala", "Guiana", "Guiné", "Guiné-Bissau", "Guiné Equatorial", "Haiti", "Holanda", "Honduras", "Hungria", "Iêmen", 
  "Ilhas Marshall", "Ilhas Maurício", "Ilhas Salomão", "Índia", "Indonésia", "Irã", "Iraque", "Irlanda", "Islândia", 
  "Israel", "Itália", "Jamaica", "Japão", "Jordânia", "Kiribati", "Kuwait", "Laos", "Lesoto", "Letônia", "Líbano", 
  "Libéria", "Líbia", "Liechtenstein", "Lituânia", "Luxemburgo", "Macedônia do Norte", "Madagascar", "Malásia", "Malaui", 
  "Maldivas", "Mali", "Malta", "Marrocos", "Mauritânia", "México", "Micronésia", "Moçambique", "Moldávia", "Mônaco", 
  "Mongólia", "Montenegro", "Myanmar", "Namíbia", "Nauru", "Nepal", "Nicarágua", "Níger", "Nigéria", "Noruega", 
  "Nova Zelândia", "Omã", "Palau", "Panamá", "Papua-Nova Guiné", "Paquistão", "Paraguai", "Peru", "Polônia", "Portugal", 
  "Quênia", "Quirguistão", "Reino Unido", "República Centro-Africana", "República Checa", "República Democrática do Congo", 
  "República Dominicana", "Romênia", "Ruanda", "Rússia", "Samoa", "San Marino", "Santa Lúcia", "São Cristóvão e Névis", 
  "São Tomé e Príncipe", "São Vicente e Granadinas", "Seychelles", "Senegal", "Serra Leoa", "Sérvia", "Singapura", "Síria", 
  "Somália", "Sri Lanka", "Suazilândia", "Sudão", "Sudão do Sul", "Suécia", "Suíça", "Suriname", "Tailândia", "Taiwan", 
  "Tajiquistão", "Tanzânia", "Timor-Leste", "Togo", "Tonga", "Trinidad e Tobago", "Tunísia", "Turcomenistão", "Turquia", 
  "Tuvalu", "Ucrânia", "Uganda", "Uruguai", "Uzbequistão", "Vanuatu", "Vaticano", "Venezuela", "Vietnã", "Zâmbia", "Zimbábue"
];

export function QuotationBasicInfoForm({ quotation, handleChange, isEditing = false, onCancel, saveQuotation, migrateOrphanPages }: BasicInfoFormProps) {
  console.log('[DEBUG][BasicInfoForm] Render - quotation.notes:', quotation.notes);
  const [destinationInputOpen, setDestinationInputOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isProcessingNotes, setIsProcessingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [tripSelectorOpen, setTripSelectorOpen] = useState(false);
  const [tripSearchValue, setTripSearchValue] = useState("");
  const [availableTrips, setAvailableTrips] = useState<Trip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const showDates = !!quotation.startDate || !!quotation.endDate;
  const { chatCompletion } = useOpenAiApi();
  const { toast } = useToast();
  
  // Estados locais para os campos de nome das abas
  const [accommodationsTabNameTemp, setAccommodationsTabNameTemp] = useState(quotation.accommodationsTabName || "Hospedagens");
  const [flightsTabNameTemp, setFlightsTabNameTemp] = useState(quotation.flightsTabName || "Voos");
  const [recommendationsTabNameTemp, setRecommendationsTabNameTemp] = useState(quotation.recommendationsTabName || "Recomendações");
  const [priceTabNameTemp, setPriceTabNameTemp] = useState(quotation.priceTabName || "Preços");
  const [customTabNameTemp, setCustomTabNameTemp] = useState(quotation.customTabName || 'Personalizada');
  
  // Atualizar estados temporários quando as props mudarem
  useEffect(() => {
    console.log('[DEBUG][BasicInfoForm] useEffect - quotation mudou:', quotation.notes);
    setAccommodationsTabNameTemp(quotation.accommodationsTabName || "Hospedagens");
    setFlightsTabNameTemp(quotation.flightsTabName || "Voos");
    setRecommendationsTabNameTemp(quotation.recommendationsTabName || "Recomendações");
    setPriceTabNameTemp(quotation.priceTabName || "Preços");
    setCustomTabNameTemp(quotation.customTabName || 'Personalizada');
  }, [quotation.accommodationsTabName, quotation.flightsTabName, quotation.recommendationsTabName, quotation.priceTabName, quotation.customTabName]);
  
  // Criando uma versão síncrona da função para uso no JSX que busca o estado atual de clientes
  const getClientNamesSync = (clientIds: string[] = []): string => {
    if (!clientIds || clientIds.length === 0) return 'Sem cliente';
    
    // Filtrar clientes do estado atual
    const validClients = clients.filter(client => clientIds.includes(client.id));
    
    if (validClients.length === 0) return 'Sem cliente';
    if (validClients.length === 1) return validClients[0].name;
    if (validClients.length === 2) return `${validClients[0].name} e ${validClients[1].name}`;
    
    // Para 3 ou mais clientes
    return `${validClients[0].name}, ${validClients[1].name} +${validClients.length - 2}`;
  };
  
  // Carregar viagens e clientes do Supabase
  useEffect(() => {
    const loadTripsAndClients = async () => {
      try {
        // Primeiro carregar clientes para garantir que estão disponíveis para exibição
        const clientsData = await clientService.getClients();
        setClients(clientsData);
        
        // Carregar viagens do Supabase
        const tripsData = await tripService.getTrips();
        const mappedTrips = tripsData.map(trip => ({
          id: trip.id,
          title: trip.title,
          clientIds: trip.client_ids,
          destination: trip.destination,
          startDate: trip.start_date ? new Date(trip.start_date) : null,
          endDate: trip.end_date ? new Date(trip.end_date) : null,
          status: trip.status,
          notes: trip.notes,
          coverImages: trip.cover_images,
          days: trip.days,
          itineraryType: trip.itinerary_type
        }));
        setAvailableTrips(mappedTrips);
        
        // Se a cotação já tem uma viagem associada, selecionar
        if (quotation.tripId) {
          const tripData = await tripService.getTripById(quotation.tripId);
          if (tripData) {
            const selectedTrip = {
              id: tripData.id,
              title: tripData.title,
              clientIds: tripData.client_ids,
              destination: tripData.destination,
              startDate: tripData.start_date ? new Date(tripData.start_date) : null,
              endDate: tripData.end_date ? new Date(tripData.end_date) : null,
              status: tripData.status,
              notes: tripData.notes,
              coverImages: tripData.cover_images,
              days: tripData.days,
              itineraryType: tripData.itinerary_type
            };
            setSelectedTrip(selectedTrip);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar viagens e clientes:', error);
        toast({
          title: "Erro ao carregar dados",
          description: "Ocorreu um erro ao buscar viagens e clientes do banco de dados.",
          variant: "destructive"
        });
      }
    };
    
    loadTripsAndClients();
  }, [quotation.tripId, toast]);
  
  // Parse the destination field (which could be a string or array) and ensure it's an array
  const destinations = typeof quotation.destination === 'string' 
    ? quotation.destination.split(',').map(d => d.trim()).filter(Boolean)
    : Array.isArray(quotation.destination) 
      ? quotation.destination 
      : [];
  
  const handleAddDestination = (country: string) => {
    if (!destinations.includes(country)) {
      const newDestinations = [...destinations, country];
      handleChange('destination', newDestinations);
    }
    setSearchValue("");
  };

  const handleRemoveDestination = (country: string) => {
    const newDestinations = destinations.filter(d => d !== country);
    handleChange('destination', newDestinations);
  };
  
  const filteredCountries = countries.filter(country => 
    country.toLowerCase().includes(searchValue.toLowerCase()) && 
    !destinations.includes(country)
  );

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    const date = value ? new Date(value) : null;
    handleChange(field, date);
  };

  const handleImageUpload = (images: string[]) => {
    handleChange('coverImages', images);
  };

  // Função para processar o texto das notas gerais usando a API da OpenAI
  const handleProcessNotesPrompt = async () => {
    if (!quotation.notes || quotation.notes.trim() === '') {
      toast({
        title: "Notas vazias",
        description: "Por favor, insira um texto nas notas para ser processado.",
        variant: "destructive"
      });
      return;
    }

    // Extrair o texto puro do HTML para enviar para a API
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = quotation.notes;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    if (!textContent.trim()) {
      toast({
        title: "Notas vazias",
        description: "Por favor, insira um texto nas notas para ser processado.",
        variant: "destructive"
      });
      return;
    }

    // Guardar o texto original caso ocorra algum erro
    const originalNotes = quotation.notes;
    setIsProcessingNotes(true);
    
    try {
      toast({
        title: "Processando texto",
        description: "Enviando suas notas para processamento...",
      });
      
      // Enviar apenas o texto do usuário, sem prompts adicionais
      const response = await chatCompletion(textContent);
      
      // Extrair a resposta do ChatGPT
      const generatedContent = response.choices[0].message.content.trim();
      
      // Atualizar o campo de notas com a resposta
      handleChange('notes', generatedContent);
      
      toast({
        title: "Texto gerado",
        description: "As notas gerais foram processadas e atualizadas com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao processar notas:', error);
      
      // Restaurar o texto original em caso de erro
      handleChange('notes', originalNotes);
      
      toast({
        title: "Erro no processamento",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao processar o texto.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingNotes(false);
    }
  };

  // Função para buscar viagens pelo nome do cliente
  const handleTripSearch = async (value: string) => {
    setTripSearchValue(value);
    
    try {
      // Se o campo de busca estiver vazio, mostrar todas as viagens
      if (!value.trim()) {
        const tripsData = await tripService.getTrips();
        setAvailableTrips(tripsData.map(trip => ({
          id: trip.id,
          title: trip.title,
          clientIds: trip.client_ids,
          destination: trip.destination,
          startDate: trip.start_date ? new Date(trip.start_date) : null,
          endDate: trip.end_date ? new Date(trip.end_date) : null,
          status: trip.status,
          notes: trip.notes,
          coverImages: trip.cover_images,
          days: trip.days,
          itineraryType: trip.itinerary_type
        })));
        return;
      }
      
      // Carregar todas as viagens e clientes para realizar a busca
      const tripsData = await tripService.getTrips();
      const clientsData = await clientService.getClients();
      
      // Buscar em todas as viagens
      const searchTerm = value.toLowerCase();
      
      const filteredTrips = tripsData.filter(trip => {
        // Obter todos os clientes desta viagem
        const tripClients = clientsData.filter(client => 
          trip.client_ids && trip.client_ids.includes(client.id)
        );
        
        // Verificar se algum nome de cliente inclui o termo de busca
        const clientNameMatches = tripClients.some(client => 
          client.name.toLowerCase().includes(searchTerm)
        );
        
        // Buscar pelo destino
        let destinationMatch = false;
        if (trip.destination) {
          if (Array.isArray(trip.destination)) {
            destinationMatch = trip.destination.some(dest => 
              dest.toLowerCase().includes(searchTerm)
            );
          } else {
            destinationMatch = typeof trip.destination === 'string' && 
              trip.destination.toLowerCase().includes(searchTerm);
          }
        }
        
        // Buscar pelo título da viagem (se existir)
        const titleMatch = trip.title ? trip.title.toLowerCase().includes(searchTerm) : false;
        
        // Retornar true se pelo menos um dos critérios for atendido
        return clientNameMatches || destinationMatch || titleMatch;
      });
      
      // Mapear os resultados para o formato esperado pelo componente
      setAvailableTrips(filteredTrips.map(trip => ({
        id: trip.id,
        title: trip.title,
        clientIds: trip.client_ids,
        destination: trip.destination,
        startDate: trip.start_date ? new Date(trip.start_date) : null,
        endDate: trip.end_date ? new Date(trip.end_date) : null,
        status: trip.status,
        notes: trip.notes,
        coverImages: trip.cover_images,
        days: trip.days,
        itineraryType: trip.itinerary_type
      })));
    } catch (error) {
      console.error('Erro ao buscar viagens:', error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar viagens.",
        variant: "destructive"
      });
    }
  };
  
  // Função para obter os nomes dos clientes de uma viagem
  const getClientNamesForTrip = async (clientIds: string[] = []) => {
    try {
      if (!clientIds.length) return 'Sem cliente';
      
      // Buscar todos os clientes de uma vez em vez de individualmente
      const allClients = await clientService.getClients();
      
      // Filtrar apenas os clientes que estão na lista de IDs
      const validClients = allClients.filter(client => 
        clientIds.includes(client.id)
      );
      
      if (validClients.length === 0) return 'Sem cliente';
      if (validClients.length === 1) return validClients[0].name;
      if (validClients.length === 2) return `${validClients[0].name} e ${validClients[1].name}`;
      
      // Para 3 ou mais clientes, mostrar os 2 primeiros e um contador para os demais
      return `${validClients[0].name}, ${validClients[1].name} +${validClients.length - 2}`;
    } catch (error) {
      console.error('Erro ao buscar nomes dos clientes:', error);
      return 'Sem cliente';
    }
  };
  
  // Função para selecionar uma viagem
  const handleSelectTrip = async (trip: Trip) => {
    setSelectedTrip(trip);
    handleChange('tripId', trip.id);
    
    // Preencher outros campos da cotação com base na viagem selecionada
    if (trip.clientIds && trip.clientIds.length > 0) {
      try {
        const clientNames = await getClientNamesForTrip(trip.clientIds);
        handleChange('client', clientNames);
      } catch (error) {
        console.error('Erro ao buscar nomes dos clientes para a viagem:', error);
        toast({
          title: "Erro ao carregar clientes",
          description: "Não foi possível carregar os dados dos clientes para esta viagem.",
          variant: "destructive"
        });
      }
    }
    
    if (trip.destination) {
      handleChange('destination', trip.destination);
    }
    
    if (trip.startDate) {
      handleChange('startDate', trip.startDate);
    }
    
    if (trip.endDate) {
      handleChange('endDate', trip.endDate);
    }
    
    setTripSelectorOpen(false);
  };

  // Adicionar função auxiliar para formatação de datas
  const formatTripStatus = (status: string) => {
    if (!status) return 'Status desconhecido';
    switch(status) {
      case 'quotation': return 'Cotação';
      case 'itinerary': return 'Roteiro';
      case 'day-by-day': return 'Day-by-Day';
      default: return status;
    }
  };

  const formatDateRange = (startDate: Date | string | null | undefined, endDate: Date | string | null | undefined) => {
    if (!startDate && !endDate) return 'Datas não definidas';

    if (startDate && endDate) {
      let start, end;
      
      try {
        start = startDate instanceof Date ? startDate : new Date(startDate);
        end = endDate instanceof Date ? endDate : new Date(endDate);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          return 'Datas inválidas';
        }
        
        return `${formatSafeDate(start, 'dd/MM/yyyy')} - ${formatSafeDate(end, 'dd/MM/yyyy')}`;
      } catch (error) {
        console.error('Error formatting date range:', error);
        return 'Datas inválidas';
      }
    }
    
    if (startDate) {
      try {
        const start = startDate instanceof Date ? startDate : new Date(startDate);
        if (!isNaN(start.getTime())) {
          return formatSafeDate(start, 'dd/MM/yyyy');
        }
      } catch (error) {
        console.error('Error formatting start date:', error);
      }
    }
    
    if (endDate) {
      try {
        const end = endDate instanceof Date ? endDate : new Date(endDate);
        if (!isNaN(end.getTime())) {
          return formatSafeDate(end, 'dd/MM/yyyy');
        }
      } catch (error) {
        console.error('Error formatting end date:', error);
      }
    }
    
    return 'Datas não definidas';
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="info">Informações Gerais</TabsTrigger>
        <TabsTrigger value="client-view">Visualização Cliente</TabsTrigger>
        <TabsTrigger value="navigation">Navegação</TabsTrigger> {/* NOVA ABA */}
        <TabsTrigger value="accommodations">Hospedagens</TabsTrigger>
        <TabsTrigger value="flights">Voos</TabsTrigger>
        <TabsTrigger value="recommendations">Recomendações</TabsTrigger>
        <TabsTrigger value="price">Preço</TabsTrigger>
        <TabsTrigger value="custom">Aba Personalizada</TabsTrigger>
      </TabsList>
      
      <TabsContent value="info">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Informações Gerais</h2>
              {!isEditing && onCancel && (
                <Button 
                  variant="outline" 
                  onClick={onCancel}
                >
                  Cancelar
                </Button>
              )}
            </div>
            
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-4">
                <label className="w-1/4 text-sm font-medium">Viagem <span className="text-red-500">*</span></label>
                <div className="flex-1">
                  <Popover open={tripSelectorOpen} onOpenChange={setTripSelectorOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={tripSelectorOpen}
                        className="w-full justify-between text-left hover:bg-gray-50"
                      >
                        {selectedTrip ? (
                          <div className="flex flex-col items-start">
                            <span className="font-medium text-gray-800">{getClientNamesSync(selectedTrip.clientIds)}</span>
                            <div className="flex items-center text-xs text-gray-500 gap-2 mt-0.5">
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-gray-400" />
                                <span>{Array.isArray(selectedTrip.destination) 
                                  ? selectedTrip.destination.join(', ') 
                                  : selectedTrip.destination || 'Sem destino'
                                }</span>
                              </div>
                              {(selectedTrip.startDate || selectedTrip.endDate) && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3 text-gray-400" />
                                  <span>{formatDateRange(selectedTrip.startDate, selectedTrip.endDate)}</span>
                                </div>
                              )}
                              {selectedTrip.status && (
                                <Badge className={cn(
                                  "text-xs border ml-1",
                                  selectedTrip.status === 'quotation' && "bg-blue-50 text-blue-700 border-blue-200",
                                  selectedTrip.status === 'itinerary' && "bg-emerald-50 text-emerald-700 border-emerald-200",
                                  selectedTrip.status === 'day-by-day' && "bg-violet-50 text-violet-700 border-violet-200"
                                )}>
                                  {formatTripStatus(selectedTrip.status)}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Selecione uma viagem</span>
                        )}
                        {selectedTrip && (
                          <X
                            className="h-4 w-4 text-gray-400 hover:text-gray-600 shrink-0 ml-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Limpar a seleção de viagem
                              setSelectedTrip(null);
                              handleChange('tripId', undefined);
                              // Limpar os campos preenchidos automaticamente
                              handleChange('client', '');
                              handleChange('destination', []);
                              handleChange('startDate', null);
                              handleChange('endDate', null);
                            }}
                          />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar viagem pelo nome do cliente..." 
                          value={tripSearchValue}
                          onValueChange={handleTripSearch}
                        />
                        <CommandList className="max-h-[300px] overflow-auto">
                          <CommandEmpty>Nenhuma viagem encontrada.</CommandEmpty>
                          <CommandGroup>
                            {availableTrips.map((trip) => (
                              <CommandItem
                                key={trip.id}
                                onSelect={() => {
                                  handleSelectTrip(trip)
                                    .catch(error => {
                                      console.error('Erro ao selecionar viagem:', error);
                                      toast({
                                        title: "Erro",
                                        description: "Não foi possível selecionar a viagem. Tente novamente.",
                                        variant: "destructive"
                                      });
                                    });
                                }}
                                className="flex flex-col items-start py-3 hover:bg-gray-100 cursor-pointer"
                              >
                                <div className="flex flex-col w-full gap-1.5 px-1">
                                  <div className="flex justify-between w-full">
                                    <span className="font-medium text-base">{getClientNamesSync(trip.clientIds)}</span>
                                    <Badge variant="outline" className={cn(
                                      "text-xs border",
                                      trip.status === 'quotation' && "bg-blue-50 text-blue-700 border-blue-200",
                                      trip.status === 'itinerary' && "bg-emerald-50 text-emerald-700 border-emerald-200",
                                      trip.status === 'day-by-day' && "bg-violet-50 text-violet-700 border-violet-200"
                                    )}>
                                      {formatTripStatus(trip.status)}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center text-xs text-gray-600 gap-1.5">
                                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                                    <span className="font-medium">{Array.isArray(trip.destination) ? trip.destination.join(', ') : trip.destination || 'Sem destino'}</span>
                                  </div>
                                  {(trip.startDate || trip.endDate) && (
                                    <div className="flex items-center text-xs text-gray-600 gap-1.5">
                                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="font-medium">{formatDateRange(trip.startDate, trip.endDate)}</span>
                                    </div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {!selectedTrip && (
                    <p className="text-xs text-red-500 mt-1">
                      Selecione uma viagem para continuar. Caso não exista, crie uma nova no painel de viagens.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="w-1/4 text-sm font-medium">Título</label>
                <Input
                  value={quotation.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Nome da cotação"
                  className="flex-1"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <label className="w-1/4 text-sm font-medium">Cliente</label>
                <Input
                  value={quotation.client}
                  onChange={(e) => handleChange('client', e.target.value)}
                  placeholder="Nome do cliente"
                  className="flex-1"
                  disabled={!!selectedTrip}
                />
                {selectedTrip && (
                  <p className="text-xs text-gray-500">
                    Preenchido automaticamente com base na viagem selecionada
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <label className="w-1/4 text-sm font-medium">Destino</label>
                <div className="flex-1">
                  <Popover open={destinationInputOpen} onOpenChange={setDestinationInputOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={destinationInputOpen}
                        className="w-full justify-between text-left"
                      >
                        {destinations.length > 0 ? `${destinations.length} países selecionados` : "Selecione os países"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Buscar país..." 
                          value={searchValue}
                          onValueChange={setSearchValue}
                        />
                        <CommandList>
                          <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                          <CommandGroup>
                            {filteredCountries.map((country) => (
                              <CommandItem
                                key={country}
                                onSelect={() => handleAddDestination(country)}
                              >
                                {country}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {destinations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {destinations.map(country => (
                        <Badge key={country} variant="secondary" className="flex items-center gap-1">
                          {country}
                          <button
                            type="button"
                            className="rounded-full ml-1 hover:bg-background/80"
                            onClick={() => handleRemoveDestination(country)}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="w-1/4 text-sm font-medium">Status</label>
                <div className="flex-1">
                  <Select
                    value={quotation.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Rascunho</SelectItem>
                      <SelectItem value="pending">Em análise</SelectItem>
                      <SelectItem value="approved">Aprovada</SelectItem>
                      <SelectItem value="rejected">Rejeitada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-1/4">
                  <label className="text-sm font-medium">Notas Gerais</label>
                </div>
                <div className="flex-1">
                  <RichTextEditor
                    value={quotation.notes || ''}
                    onChange={(value) => {
                      console.log('[DEBUG][BasicInfoForm] handleChange notes - novo valor:', value);
                      handleChange('notes', value);
                    }}
                    placeholder="Observações gerais sobre a cotação"
                    className="min-h-[100px]"
                    rows={6}
                    onProcessWithAI={handleProcessNotesPrompt}
                    isProcessing={isProcessingNotes}
                  />
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-1/4">
                  <label className="text-sm font-medium">Imagem de Capa</label>
                </div>
                <div className="flex-1">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsUploadModalOpen(true)}
                    className="w-full h-32 border-dashed flex flex-col items-center justify-center gap-2"
                  >
                    <ImagePlus className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {quotation.coverImages && quotation.coverImages.length > 0 
                        ? "Clique para alterar as imagens" 
                        : "Clique para adicionar imagens"}
                    </span>
                  </Button>
                  
                  {quotation.coverImages && quotation.coverImages.length > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <div 
                        className="h-12 w-12 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-[#087a83] cursor-pointer transition-all hover:scale-105"
                        onClick={() => setIsUploadModalOpen(true)}
                      >
                        <img 
                          src={quotation.coverImages[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {quotation.coverImages.length > 1 && (
                        <span 
                          className="text-sm text-gray-500 cursor-pointer hover:text-[#087a83]"
                          onClick={() => setIsUploadModalOpen(true)}
                        >
                          +{quotation.coverImages.length - 1} imagens
                        </span>
                      )}
                    </div>
                  )}
                  
                  <ImageManagerModal
                    isOpen={isUploadModalOpen}
                    onClose={() => setIsUploadModalOpen(false)}
                    images={quotation.coverImages || []}
                    onImagesChange={handleImageUpload}
                    contextType="quotation"
                    contextId={quotation.id}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="client-view">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Configurações de Visualização do Cliente</h2>
            
            <div className="flex flex-col space-y-5">
              <div className="border-b pb-4">
                <StyledSectionTitle icon={Navigation}>
                  Navegação
                </StyledSectionTitle>
                
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4 text-right">Modo de Exibição</label>
                  <div className="flex items-center gap-2 w-[70%]">
                    <Switch 
                      id="page-day-mode"
                      checked={!!quotation.useDayMode} 
                      onCheckedChange={(checked) => handleChange('useDayMode', checked)}
                    />
                    <Label htmlFor="page-day-mode" className="text-sm font-medium">
                      {quotation.useDayMode ? "Modo Dias" : "Modo Páginas"}
                    </Label>
                    <span className="text-xs text-gray-500 ml-3">
                      {quotation.useDayMode 
                        ? "As páginas serão exibidas como dias do roteiro." 
                        : "As páginas serão exibidas como seções do documento."}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4 text-right">Navegação de Páginas</label>
                  <div className="flex items-center gap-2 w-[70%]">
                    <Switch 
                      id="use-page-dropdown"
                      checked={!!quotation.usePageDropdown} 
                      onCheckedChange={(checked) => handleChange('usePageDropdown', checked)}
                    />
                    <Label htmlFor="use-page-dropdown" className="text-sm font-medium">
                      {quotation.usePageDropdown ? "Dropdown de páginas" : "Tabs de páginas"}
                    </Label>
                    <span className="text-xs text-gray-500 ml-3">
                      {quotation.usePageDropdown 
                        ? "Recomendado para muitos dias/páginas." 
                        : "Ideal para poucos dias/páginas."}
                    </span>
                  </div>
                </div>
                
                {quotation.usePageDropdown && (
                  <div className="flex items-center mb-3">
                    <div className="w-[30%]"></div>
                    <div className="w-[70%] text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Dica:</strong> Você pode configurar páginas individuais para serem mostradas como tabs mesmo com o dropdown ativado. 
                      Para isso, edite a página desejada e habilite a opção "Sempre exibir como tab individual" nas configurações da página.
                    </div>
                  </div>
                )}
              </div>
              
              <div className="border-b pb-4">
                <StyledSectionTitle icon={Map}>
                  Mapas
                </StyledSectionTitle>
                
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4 text-right">Opções do Mapa</label>
                  <div className="flex items-center gap-2 w-[70%]">
                    <Switch 
                      id="show-map-lines"
                      checked={quotation.showMapLines !== false} 
                      onCheckedChange={(checked) => handleChange('showMapLines', checked)}
                    />
                    <Label htmlFor="show-map-lines" className="text-sm font-medium">
                      Mostrar linhas no mapa
                    </Label>
                    <span className="text-xs text-gray-500 ml-3">
                      Exibe linhas conectando os pontos no roteiro.
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="border-b pb-4">
                <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                  <Layers className="h-4 w-4 text-voah-coral flex-shrink-0" />
                  <span className="whitespace-nowrap">Visibilidade e Nomes das Abas</span>
                </h3>
                
                {/* Aba de Hospedagens */}
                <div className="flex items-center mb-4">
                  <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Hospedagens</label>
                  <div className="flex flex-wrap items-center gap-4 w-[70%]">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-accommodations-tab"
                        checked={quotation.showAccommodationsTab !== false} 
                        onCheckedChange={(checked) => handleChange('showAccommodationsTab', checked)}
                      />
                      <Label htmlFor="show-accommodations-tab" className="text-sm font-medium">
                        Mostrar aba
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="accommodations-tab-name" className="text-sm text-gray-500">
                        Nome:
                      </Label>
                      <Input
                        id="accommodations-tab-name"
                        value={accommodationsTabNameTemp}
                        onChange={(e) => setAccommodationsTabNameTemp(e.target.value)}
                        onBlur={() => {
                          const value = accommodationsTabNameTemp.trim() || "Hospedagens";
                          setAccommodationsTabNameTemp(value);
                          handleChange('accommodationsTabName', value);
                        }}
                        placeholder="Nome da aba"
                        className="w-[200px] h-8"
                        disabled={quotation.showAccommodationsTab === false}
                      />
                    </div>
                  </div>
                </div>

                {/* Aba de Voos */}
                <div className="flex items-center mb-4">
                  <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Voos</label>
                  <div className="flex flex-wrap items-center gap-4 w-[70%]">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-flights-tab"
                        checked={quotation.showFlightsTab !== false} 
                        onCheckedChange={(checked) => handleChange('showFlightsTab', checked)}
                      />
                      <Label htmlFor="show-flights-tab" className="text-sm font-medium">
                        Mostrar aba
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="flights-tab-name" className="text-sm text-gray-500">
                        Nome:
                      </Label>
                      <Input
                        id="flights-tab-name"
                        value={flightsTabNameTemp}
                        onChange={(e) => setFlightsTabNameTemp(e.target.value)}
                        onBlur={() => {
                          const value = flightsTabNameTemp.trim() || "Voos";
                          setFlightsTabNameTemp(value);
                          handleChange('flightsTabName', value);
                        }}
                        placeholder="Nome da aba"
                        className="w-[200px] h-8"
                        disabled={quotation.showFlightsTab === false}
                      />
                    </div>
                  </div>
                </div>

                {/* Aba de Recomendações */}
                <div className="flex items-center mb-4">
                  <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Recomendações</label>
                  <div className="flex flex-wrap items-center gap-4 w-[70%]">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-recommendations-tab"
                        checked={!!quotation.showRecommendationsTab} 
                        onCheckedChange={(checked) => handleChange('showRecommendationsTab', checked)}
                      />
                      <Label htmlFor="show-recommendations-tab" className="text-sm font-medium">
                        Mostrar aba
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="recommendations-tab-name" className="text-sm text-gray-500">
                        Nome:
                      </Label>
                      <Input
                        id="recommendations-tab-name"
                        value={recommendationsTabNameTemp}
                        onChange={(e) => setRecommendationsTabNameTemp(e.target.value)}
                        onBlur={() => {
                          const value = recommendationsTabNameTemp.trim() || "Recomendações";
                          setRecommendationsTabNameTemp(value);
                          handleChange('recommendationsTabName', value);
                        }}
                        placeholder="Nome da aba"
                        className="w-[200px] h-8"
                        disabled={!quotation.showRecommendationsTab}
                      />
                    </div>
                  </div>
                </div>

                {/* Aba de Preços */}
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Preços</label>
                  <div className="flex flex-wrap items-center gap-4 w-[70%]">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-price-tab"
                        checked={!!quotation.showPriceTab} 
                        onCheckedChange={(checked) => handleChange('showPriceTab', checked)}
                      />
                      <Label htmlFor="show-price-tab" className="text-sm font-medium">
                        Mostrar aba
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="price-tab-name" className="text-sm text-gray-500">
                        Nome:
                      </Label>
                      <Input
                        id="price-tab-name"
                        value={priceTabNameTemp}
                        onChange={(e) => setPriceTabNameTemp(e.target.value)}
                        onBlur={() => {
                          const value = priceTabNameTemp.trim() || "Preços";
                          setPriceTabNameTemp(value);
                          handleChange('priceTabName', value);
                        }}
                        placeholder="Nome da aba"
                        className="w-[200px] h-8"
                        disabled={!quotation.showPriceTab}
                      />
                    </div>
                  </div>
                </div>

                {/* Aba Personalizada */}
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba Personalizada</label>
                  <div className="flex flex-wrap items-center gap-4 w-[70%]">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-custom-tab"
                        checked={!!quotation.showCustomTab} 
                        onCheckedChange={(checked) => handleChange('showCustomTab', checked)}
                      />
                      <Label htmlFor="show-custom-tab" className="text-sm font-medium">
                        Mostrar aba
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="custom-tab-name" className="text-sm text-gray-500">
                        Nome:
                      </Label>
                      <Input
                        id="custom-tab-name"
                        value={customTabNameTemp}
                        onChange={(e) => setCustomTabNameTemp(e.target.value)}
                        onBlur={() => {
                          const value = customTabNameTemp.trim() || "Personalizada";
                          setCustomTabNameTemp(value);
                          handleChange('customTabName', value);
                        }}
                        placeholder="Nome da aba"
                        className="w-[200px] h-8"
                        disabled={!quotation.showCustomTab}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {quotation.useDayMode && (
                <div className="border-b pb-4">
                  <StyledSectionTitle icon={Calendar}>
                    Datas
                  </StyledSectionTitle>
                  
                  <div className="flex items-center mb-3">
                    <label className="text-sm font-medium w-[30%] pr-4 text-right">Datas do Roteiro</label>
                    <div className="flex items-center gap-2 w-[70%]">
                      <Checkbox 
                        id="use-defined-dates"
                        checked={!!quotation.startDate}
                        onCheckedChange={(checked) => {
                          if (!checked) {
                            handleChange('startDate', null);
                            handleChange('endDate', null);
                          } else {
                            handleChange('startDate', new Date());
                          }
                        }}
                      />
                      <Label htmlFor="use-defined-dates" className="text-sm font-medium">
                        Usar data definida
                      </Label>
                      <span className="text-xs text-gray-500 ml-3">
                        Define data de início para calcular os dias do roteiro.
                      </span>
                    </div>
                  </div>
                  
                  {quotation.startDate && (
                    <div className="flex items-center pl-[30%]">
                      <div className="flex flex-col gap-1 mt-3">
                        <Label htmlFor="start-date" className="text-sm whitespace-nowrap">
                          Data de início
                        </Label>
                        <Input
                          type="date"
                          id="start-date"
                          value={formatSafeDate(quotation.startDate, 'yyyy-MM-dd')}
                          onChange={(e) => {
                            const date = new Date(e.target.value);
                            handleChange('startDate', date);
                          }}
                          className="w-[180px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1 mt-3 ml-4">
                        <Label htmlFor="end-date" className="text-sm whitespace-nowrap">
                          Data de fim
                        </Label>
                        <Input
                          type="date"
                          id="end-date"
                          value={formatSafeDate(quotation.endDate, 'yyyy-MM-dd')}
                          onChange={(e) => {
                            const date = new Date(e.target.value);
                            handleChange('endDate', date);
                          }}
                          className="w-[180px]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Botão de salvar */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={async () => {
                    handleChange('updatedAt', new Date());
                    // Logs de diagnóstico para verificar valores antes de salvar
                    console.log('Salvando configurações da cotação:', {
                      showPriceTab: quotation.showPriceTab,
                      priceTabName: quotation.priceTabName,
                      id: quotation.id
                    });
                    
                    // Chama a função saveQuotation para persistir as alterações no banco de dados
                    if (saveQuotation) {
                      const success = await saveQuotation();
                      if (success) {
                        toast({
                          title: "Configurações salvas",
                          description: "As configurações de visualização foram salvas com sucesso.",
                        });
                      }
                    } else {
                      toast({
                        title: "Configurações salvas",
                        description: "As configurações de visualização foram atualizadas.",
                      });
                    }
                  }}
                  className="bg-voah-teal hover:bg-voah-teal-dark"
                >
                  Salvar Configurações
                </Button>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="navigation">
        {/* Organização de páginas em grupos nomináveis, drag-and-drop, salvar/cancelar */}
        <NavigationTab
          quotation={quotation}
          handleChange={handleChange}
          saveQuotation={saveQuotation}
          migrateOrphanPages={migrateOrphanPages}
        />
      </TabsContent>
      
      <TabsContent value="accommodations">
        <div className="bg-white rounded-lg shadow">
          <div className="p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Hospedagens</h1>
            <div className="h-1 w-16 bg-voah-coral mb-8"></div>

            <div className="flex flex-col space-y-8">
              {/* Opções de Visualização */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Opções de Visualização</h2>
                <div className="flex items-start gap-3 mb-2">
                  <Switch 
                    id="show-accommodations-in-itinerary"
                    checked={!!quotation.showAccommodationsInItinerary} 
                    onCheckedChange={(checked) => handleChange('showAccommodationsInItinerary', checked)}
                  />
                  <div className="flex flex-col">
                    <Label htmlFor="show-accommodations-in-itinerary" className="text-base font-medium text-gray-700">
                      Exibir hospedagens no roteiro diário
                    </Label>
                    <span className="text-sm text-gray-500">
                      Mostra informações de hospedagem junto com os outros eventos no roteiro diário.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <QuotationAccommodationsTab 
            quotation={quotation} 
            handleChange={handleChange} 
            isEditing={isEditing}
          />
        </div>
      </TabsContent>
      
      <TabsContent value="flights">
        <div className="bg-white rounded-lg shadow">
          <div className="p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Voos</h1>
            <div className="h-1 w-16 bg-voah-coral mb-8"></div>
          </div>
        </div>

        <div className="mt-6">
          <QuotationFlightsTab 
            quotation={quotation} 
            handleChange={handleChange} 
            isEditing={isEditing}
          />
        </div>
      </TabsContent>
      
      <TabsContent value="recommendations">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <RecommendationsTab 
              quotation={quotation}
              handleChange={handleChange}
            />
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="price">
        <QuotationPriceTab 
          quotation={quotation} 
          handleChange={handleChange} 
          isEditing={isEditing} 
        />
      </TabsContent>
      
      <TabsContent value="custom">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <CustomTab 
              quotation={quotation}
              handleChange={handleChange}
              isEditing={true}
            />
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
} 

function NavigationTab({ quotation, handleChange, saveQuotation, migrateOrphanPages }) {
  // Estado local para edição
  const { toast } = useToast();
  const getSafePageGroups = () => {
    // Garante que page_groups, groups e ungrouped nunca sejam undefined
    const pages = quotation.pages?.map(p => p.id) || [];
    const pg = quotation.page_groups;
    if (!pg || (!Array.isArray(pg.groups) && !Array.isArray(pg.ungrouped))) {
      return { groups: [], ungrouped: pages };
    }
    // Corrige se algum dos campos estiver undefined
    return {
      groups: Array.isArray(pg.groups) ? pg.groups : [],
      ungrouped: Array.isArray(pg.ungrouped) ? pg.ungrouped : pages
    };
  };
  const [localGroups, setLocalGroups] = React.useState(getSafePageGroups);
 
  // Sincronizar localGroups sempre que pages mudar
  React.useEffect(() => {
    // Pega todos os IDs de páginas existentes
    const allPageIds = quotation.pages?.map(p => p.id) || [];
    // IDs já alocados em grupos
    const groupedIds = (localGroups.groups || []).flatMap(g => g.pageIds);
    // IDs que não estão em nenhum grupo
    const ungrouped = allPageIds.filter(id => !groupedIds.includes(id));
    // Remover IDs de páginas que não existem mais
    const newGroups = (localGroups.groups || []).map(g => ({
      ...g,
      pageIds: g.pageIds.filter(id => allPageIds.includes(id))
    }));
    setLocalGroups({ groups: newGroups, ungrouped });
  }, [quotation.pages]);
  const [editingGroupId, setEditingGroupId] = React.useState(null);
  const [groupNameDraft, setGroupNameDraft] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
 
  // Atualiza isDirty se localGroups mudar
  React.useEffect(() => {
    setIsDirty(
      JSON.stringify(localGroups) !== JSON.stringify(getSafePageGroups())
    );
  }, [localGroups, quotation.page_groups, quotation.pages]);
 
  // Helper para obter páginas por id
  const getPageById = (id) => quotation.pages?.find(p => p.id === id);
 
  // Novo layout: lista única (coluna única) de grupos e páginas
  // Cada item pode ser um grupo (com páginas dentro) ou uma página solta
  // O estado local agora é um array ordenado de items: { type: 'group' | 'page', id, ... }
 
  // Função para converter page_groups em lista linear
  const toLinearList = (pageGroups, pages) => {
    if (pageGroups && Array.isArray((pageGroups as any).linearList)) {
      // Respeitar ordem persistida em linearList
      const list: any[] = [];
      const allGroups: any[] = Array.isArray((pageGroups as any).groups) ? (pageGroups as any).groups : [];
      (pageGroups as any).linearList.forEach((item: any) => {
        if (item.type === 'group') {
          const g: any = allGroups.find((gr: any) => gr.id === item.id);
          if (g) {
            list.push({ type: 'group', id: g.id, name: g.name, pageIds: Array.isArray(g.pageIds) ? g.pageIds : [] });
          }
        } else if (item.type === 'page') {
          // Somente incluir páginas que ainda existem
          const exists = pages?.some((p: any) => p.id === item.id);
          if (exists) {
            list.push({ type: 'page', id: item.id });
          }
        }
      });
      // Acrescentar quaisquer novas páginas que ainda não estejam presentes na lista linear
      const allPageIds = (pages || []).map((p: any) => p.id);
      const presentIds = new Set<string>();
      list.forEach((it: any) => {
        if (it.type === 'page') presentIds.add(it.id);
        if (it.type === 'group') (it.pageIds || []).forEach((pid: string) => presentIds.add(pid));
      });
      const missing = allPageIds.filter((pid: string) => !presentIds.has(pid));
      missing.forEach((pid: string) => list.push({ type: 'page', id: pid }));
      return list;
    }
    // Fallback: grupos primeiro e depois páginas soltas
    const list: any[] = [];
    ((pageGroups as any).groups || []).forEach((group: any) => {
      list.push({ type: 'group', id: group.id, name: group.name, pageIds: group.pageIds });
    });
    ((pageGroups as any).ungrouped || []).forEach((pid: any) => {
      list.push({ type: 'page', id: pid });
    });
    return list;
  };
 
  // Função para converter lista linear de volta para page_groups
  const fromLinearList = (list) => {
    const groups = [];
    const ungrouped = [];
    const linearList = [];
    list.forEach(item => {
      if (item.type === 'group') {
        groups.push({ id: item.id, name: item.name, pageIds: item.pageIds });
        linearList.push({ type: 'group', id: item.id });
      } else if (item.type === 'page') {
        ungrouped.push(item.id);
        linearList.push({ type: 'page', id: item.id });
      }
    });
    return { groups, ungrouped, linearList };
  };
 
  // Estado local: lista linear
  const [linearList, setLinearList] = React.useState(() => toLinearList(quotation.page_groups || getSafePageGroups(), quotation.pages));
 
  // Sincronizar linearList quando houver mudança de cotação ou de estrutura de grupos
  React.useEffect(() => {
    setLinearList(toLinearList(quotation.page_groups || getSafePageGroups(), quotation.pages));
  }, [quotation.id, quotation.page_groups]);
 
  // Manter linearList em sincronia quando páginas forem adicionadas/removidas,
  // preservando a ordem existente e acrescentando novas páginas como itens soltos
  React.useEffect(() => {
    const allPageIds = (quotation.pages || []).map(p => p.id);
    const validIdSet = new Set(allPageIds);
 
    // Coletar IDs presentes atualmente na linearList
    const presentIds = new Set<string>();
    linearList.forEach((item: any) => {
      if (item.type === 'page') presentIds.add(item.id);
      if (item.type === 'group') (item.pageIds || []).forEach((pid: string) => presentIds.add(pid));
    });
 
    const missing = allPageIds.filter(id => !presentIds.has(id));
 
    // Construir nova lista removendo páginas inexistentes e adicionando as novas ao final
    let newList: any[] = linearList
      .map(it => it.type === 'group' ? ({ ...it, pageIds: (it.pageIds || []).filter((pid: string) => validIdSet.has(pid)) }) : it)
      .filter(it => !(it.type === 'page' && !validIdSet.has(it.id)));
 
    if (missing.length > 0) {
      newList = [...newList, ...missing.map(id => ({ type: 'page', id }))];
    }
 
    const changed = JSON.stringify(newList) !== JSON.stringify(linearList);
    if (changed) {
      setLinearList(newList);
      handleChange('page_groups', fromLinearList(newList));
    }
  }, [quotation.pages, linearList, handleChange]);
 
  // Novo handleDragEnd para suportar DND dentro de grupos
  function handleDragEnd(result) {
    if (!result.destination) return;
    const { source, destination, type, draggableId } = result;
 
    // DND entre grupos/páginas soltas (lista linear principal)
    if (type === 'item') {
      const sourceIdx = source.index;
      const destIdx = destination.index;
      const newList = Array.from(linearList);
      const [removed] = newList.splice(sourceIdx, 1);
      newList.splice(destIdx, 0, removed);
      setLinearList(newList);
      handleChange('page_groups', fromLinearList(newList));
      return;
    }
 
    // DND dentro de um grupo (ordenar páginas)
    if (type.startsWith('group-pages-')) {
      const groupId = type.replace('group-pages-', '');
      const groupIdx = linearList.findIndex(item => item.type === 'group' && item.id === groupId);
      if (groupIdx === -1) return;
      const group = linearList[groupIdx];
      const newPageIds = Array.from(group.pageIds);
      const [removed] = newPageIds.splice(source.index, 1);
      newPageIds.splice(destination.index, 0, removed);
      // Atualiza o grupo na lista linear
      const newList = linearList.map((item, idx) =>
        idx === groupIdx ? { ...item, pageIds: newPageIds } : item
      );
      setLinearList(newList);
      handleChange('page_groups', fromLinearList(newList));
      return;
    }
  }
 
  // Adicionar grupo no topo
  function handleAddGroup() {
    const newId = `group-${Date.now()}`;
    const newList = [{ type: 'group', id: newId, name: 'Novo Grupo', pageIds: [] }, ...linearList];
    setLinearList(newList);
    handleChange('page_groups', fromLinearList(newList));
    setEditingGroupId(newId);
    setGroupNameDraft('Novo Grupo');
  }
 
  // Remover grupo e devolver páginas para ungrouped (no topo)
  function handleRemoveGroup(groupId) {
    setLinearList(l => {
      const idx = l.findIndex(item => item.type === 'group' && item.id === groupId);
      if (idx === -1) return l;
      const group = l[idx];
      const pagesToUngroup = group.pageIds || [];
      const newList = [
        ...pagesToUngroup.map(pid => ({ type: 'page', id: pid })),
        ...l.slice(0, idx),
        ...l.slice(idx + 1)
      ];
      handleChange('page_groups', fromLinearList(newList));
      return newList;
    });
  }
 
  // Mover página para dentro de grupo
  function movePageToGroup(pageId, groupId) {
    setLinearList(l => {
      // Remove página da lista
      let newList = l.filter(item => !(item.type === 'page' && item.id === pageId));
      // Adiciona página ao grupo
      newList = newList.map(item => {
        if (item.type === 'group' && item.id === groupId) {
          return { ...item, pageIds: [...item.pageIds, pageId] };
        }
        return item;
      });
      handleChange('page_groups', fromLinearList(newList));
      return newList;
    });
  }
 
  // Mover página para fora de grupo (vira página solta na posição do grupo)
  function movePageOutOfGroup(pageId, groupId) {
    setLinearList(l => {
      let newList = [];
      l.forEach(item => {
        if (item.type === 'group' && item.id === groupId) {
          // Remove página do grupo
          const newPageIds = item.pageIds.filter(pid => pid !== pageId);
          newList.push({ ...item, pageIds: newPageIds });
          // Página solta logo após o grupo
          newList.push({ type: 'page', id: pageId });
        } else {
          newList.push(item);
        }
      });
      handleChange('page_groups', fromLinearList(newList));
      return newList;
    });
  }
 
  // Editar nome do grupo
  function handleEditGroupName(groupId, name) {
    setLinearList(l => {
      const newList = l.map(item =>
        item.type === 'group' && item.id === groupId ? { ...item, name } : item
      );
      handleChange('page_groups', fromLinearList(newList));
      return newList;
    });
    setEditingGroupId(null);
    setGroupNameDraft('');
  }
 
  // Renderização
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Organização das Páginas (Navegação)</h2>
      
      {/* Botão para migrar páginas órfãs */}
      {migrateOrphanPages && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">
            Se você tem páginas que não aparecem na navegação, clique abaixo para recuperá-las automaticamente:
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const recovered = migrateOrphanPages();
              if (!recovered) {
                toast({
                  title: "Nenhuma página órfã encontrada",
                  description: "Todas as páginas já estão no sistema de navegação.",
                });
              }
            }}
            className="bg-white"
          >
            🔄 Recuperar páginas órfãs
          </Button>
        </div>
      )}
      
      <Button onClick={handleAddGroup} className="mb-4">+ Novo Grupo</Button>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="navigation-list" type="item">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {linearList.map((item, idx) => {
                if (item.type === 'group') {
                  // Filtrar pageIds para só exibir páginas existentes e visíveis
                  const visiblePageIds = item.pageIds.filter(pid => {
                    const page = quotation.pages.find(p => p.id === pid);
                    return page && page.isVisible !== false;
                  });
                  // Se o grupo não tem páginas visíveis, não renderizar
                  if (visiblePageIds.length === 0) return null;
                  return (
                    <Draggable key={item.type + '-' + item.id} draggableId={item.type + '-' + item.id} index={idx}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={cn(
                            "mb-2 bg-gray-50 rounded shadow-sm p-2",
                            "border-2 border-voah-teal"
                          )}
                        >
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {editingGroupId === item.id ? (
                                <>
                                  <input
                                    value={groupNameDraft}
                                    onChange={e => setGroupNameDraft(e.target.value)}
                                    onBlur={() => handleEditGroupName(item.id, groupNameDraft)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') handleEditGroupName(item.id, groupNameDraft);
                                      if (e.key === 'Escape') setEditingGroupId(null);
                                    }}
                                    className="border rounded px-2 py-1 text-sm"
                                    autoFocus
                                  />
                                  <Button size="icon" variant="ghost" onClick={() => handleEditGroupName(item.id, groupNameDraft)}><Check /></Button>
                                  <Button size="icon" variant="ghost" onClick={() => setEditingGroupId(null)}><XIcon /></Button>
                                </>
                              ) : (
                                <>
                                  <span className="font-semibold text-voah-teal cursor-pointer" onClick={() => { setEditingGroupId(item.id); setGroupNameDraft(item.name); }}>{item.name}</span>
                                  <Button size="icon" variant="ghost" onClick={() => handleRemoveGroup(item.id)}><XIcon className="text-red-500" /></Button>
                                </>
                              )}
                            </div>
                            {/* Páginas dentro do grupo - agora com DND */}
                            <Droppable droppableId={`group-${item.id}-pages`} type={`group-pages-${item.id}`}>
                              {(providedPages) => (
                                <div className="ml-4" ref={providedPages.innerRef} {...providedPages.droppableProps}>
                                  {visiblePageIds.length === 0 && <div className="text-xs text-gray-400">(Adicione páginas aqui)</div>}
                                  {visiblePageIds.map((pid, pageIdx) => {
                                    const page = quotation.pages.find(p => p.id === pid);
                                    if (!page) return null;
                                    return (
                                      <Draggable key={pid} draggableId={`group-${item.id}-page-${pid}`} index={pageIdx}>
                                        {(providedPage) => (
                                          <div
                                            ref={providedPage.innerRef}
                                            {...providedPage.draggableProps}
                                            {...providedPage.dragHandleProps}
                                            className="flex items-center gap-2 mb-1 bg-white rounded px-2 py-1 border border-gray-200"
                                          >
                                            <span className="text-gray-700">{page.title}</span>
                                            <Button size="icon" variant="ghost" onClick={() => movePageOutOfGroup(pid, item.id)}><XIcon className="text-gray-400" /></Button>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {providedPages.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                } else if (item.type === 'page') {
                  // Página solta: só exibir se existir e for visível
                  const page = quotation.pages.find(p => p.id === item.id);
                  if (!page || page.isVisible === false) return null;
                  return (
                    <Draggable key={item.type + '-' + item.id} draggableId={item.type + '-' + item.id} index={idx}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className="mb-2 bg-gray-50 rounded shadow-sm p-2"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700">{page.title}</span>
                            {/* Botão para mover para grupo */}
                            {linearList.some(i => i.type === 'group') && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button size="icon" variant="ghost"><ChevronDown /></Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-40 p-2">
                                  {linearList.filter(i => i.type === 'group').map(g => (
                                    <div key={g.id} className="flex items-center gap-2 mb-1">
                                      <Button size="sm" variant="ghost" onClick={() => movePageToGroup(item.id, g.id)}>{g.name}</Button>
                                    </div>
                                  ))}
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                }
                return null;
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="flex gap-2 mt-6 justify-end">
        <Button variant="secondary" onClick={() => setLinearList(toLinearList(quotation.page_groups || getSafePageGroups(), quotation.pages))}>Cancelar</Button>
        <Button onClick={() => { saveQuotation && saveQuotation(); }}>Salvar</Button>
      </div>
    </div>
  );
} 